import S, { DataSignal } from 's-js';
import { AssertExtendedBy } from 'lib/assertExtends';
import { DraftSignal } from 'lib/draftSignals';

import { BerthReservation } from 'data/models/BerthReservation';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { Measurement } from 'data/models/Measurement';
import { VesselSide } from 'server/enums/BerthReservation';

export interface FootmarkAssignmentsParams {
    Reservation : FootmarkAssignmentsReservation;
    CanEdit : () => boolean;
    /**
     * When should the `FootmarkAssignments` widget push footmarks updates to the provided `Reservation`?
     * Defaults to `FootmarksUpdateTrigger.OnChange`.
     */
    FootmarksUpdateTrigger? : FootmarksUpdateTrigger;
}

export enum FootmarksUpdateTrigger {
    /**
     * Write footmarks edits to the provided `Reservation` on each change event (i.e. w/ every keystroke).
     */
    OnChange = 'on-change',
    /**
     * Write footmarks edits to the provided `Reservation` only when the user changes focus away from the
     * startmark and endmark inputs.
     */
    OnBlur = 'on-blur',
}

interface _FootmarkAssignmentsReservation {
    Footmark : Measurement;
    Length : Measurement;
    Itinerary : () => ItineraryPoint | null;
    SideTo : DataSignal<VesselSide>;
}
export type FootmarkAssignmentsReservation = AssertExtendedBy<_FootmarkAssignmentsReservation, BerthReservation>;

export type FootmarkAssignments = ReturnType<typeof FootmarkAssignments>;
export function FootmarkAssignments(
    {
        Reservation,
        CanEdit,
        FootmarksUpdateTrigger: FootmarksTrigger = FootmarksUpdateTrigger.OnChange
    } : FootmarkAssignmentsParams
) {
    const
        // Footmarks drive important business logic server-side (overlap detection, most-utilized-berth rule).
        // So, to avoid unnecessary work server-side (recomputing issues, recomputing assigned berth, sending
        // notifications about changes from the above), we provide an option to only push footmark edits to the
        // provided Reservation model on specific triggers.
        draftStartmark = DraftSignal((num? : number | null) => {
            if (num === undefined) {
                return Reservation.Footmark.Num();
            } else {
                if (num !== null) {
                    return Reservation.Footmark.Num(num);
                } else {
                    // Don't write a null value to the underlying Footmark.Num signal. This supports a UX
                    // whereby clearing either input element and then changing focus counts as cancelling the
                    // footmark edit.
                    return num;
                }
            }
        }),
        resLength = S(() => Reservation.Length.convert(Reservation.Footmark.UOM())),
        draftEndmark = (num? : number | null) => {
            if (num === undefined) {
                return draftStartmark() === null ? null : draftStartmark()! + resLength().Num();
            } else {
                draftStartmark(S.sample(() => num === null ? null : num - resLength().Num()));
                return num;
            }
        };

    if (FootmarksTrigger === FootmarksUpdateTrigger.OnChange) {
        S(() => draftStartmark.isCaching() && draftStartmark.checkpoint().commit());
    }

    return {
        Reservation,
        CanEdit,
        StartmarkNum,
        EndmarkNum,
        CommitFootmarks
    };

    function StartmarkNum(num? : number | null) {
        if (num === undefined) {
            return draftStartmark() === null ? null : Math.round(draftStartmark()!);
        } else {
            return draftStartmark(num);
        }
    }

    function EndmarkNum(num? : number | null) {
        if (num === undefined) {
            return draftEndmark() === null ? null : Math.round(draftEndmark()!);
        } else {
            return draftEndmark(num);
        }
    }

    function CommitFootmarks() {
        draftStartmark.checkpoint().commit();
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/footmarkassignments/model.ts